import { captalizeString, padStart } from './util';

export enum DatePatternFragment {
    DAY = 'dd',
    MONTH = 'MM',
    ABBREVIATED_MONTH_NAME = 'MMM',
    MONTH_NAME = 'MMMM',
    YEAR = 'yyyy',
    HOUR = 'HH',
    MINUTE = 'mm',
    SECOND = 'ss',
    MILLISECOND = 'SSSSSS',
    TIMEZONE = 'Z',
}

export enum DatePattern {
    DATE = 'dd/MM/yyyy',
    TIME = 'HH:mm:ss',
    DATE_TIME = 'dd/MM/yyyy HH:mm:ss',
    AMERICAN_PATTERN = 'MM/dd/yyyy',
    AMERICAN_PATTERN_TIME = 'MM/dd/yyyy HH:mm:ss',
    LOCAL_DATE = 'yyyy-MM-dd',
    LOCAL_DATE_TIME = 'yyyy-MM-ddTHH:mm:ss.SSSSSS',
    LOCAL_DATE_TIME_TIME_ZONE = 'yyyy-MM-ddTHH:mm:ss.SSSSSS Z',
    MONTH_YEAR = 'MM/yyyy',
}

export enum Months {
    JAN = 'JANEIRO',
    FEV = 'FEVEREIRO',
    MAR = 'MARÇO',
    ABR = 'ABRIL',
    MAI = 'MAIO',
    JUN = 'JUNHO',
    JUL = 'JULHO',
    AGO = 'AGOSTO',
    SET = 'SETEMBRO',
    OUT = 'OUTUBRO',
    NOV = 'NOVEMBRO',
    DEZ = 'DEZEMBRO',
}

export type MonthKey = keyof typeof Months;

export class DateFormat {
    /**
     * Formata uma data para uma string personalizada de acordo com o formato especificado.
     *
     * @param {Date | string} date - A data a ser formatada.
     * @param {string} pattern - O formato desejado da string de saída.
     * @returns {string} A data formatada como uma string.
     *
     * Obs: se for usar um pattern recorrente, adicione no Enum DatePattern
     */
    public static formatDateTime = (
        date: Date | string,
        pattern: DatePattern | DatePatternFragment | string = DatePattern.DATE,
    ): string => {
        const dateCopy = new Date(date);
        const year = dateCopy.getFullYear();
        const month = padStart(dateCopy.getMonth() + 1, 2);
        const day = padStart(dateCopy.getDate(), 2);
        const hours = padStart(dateCopy.getHours(), 2);
        const seconds = padStart(dateCopy.getSeconds(), 2);
        const minutes = padStart(dateCopy.getMinutes(), 2);
        const milliseconds = padStart(dateCopy.getMilliseconds(), 3) + '000';

        const timezoneOffset = -dateCopy.getTimezoneOffset();
        const timezoneSign = timezoneOffset >= 0 ? '+' : '-';
        const timezoneHours = padStart(Math.floor(Math.abs(timezoneOffset) / 60), 2);
        const timezoneMinutes = padStart(Math.abs(timezoneOffset) % 60, 2);
        const timezone = `${timezoneSign}${timezoneHours}:${timezoneMinutes}`;

        const monthKey = Object.keys(Months)[dateCopy.getMonth()] as MonthKey;
        return pattern
            .replace('yyyy', year.toString())
            .replace('MMMM', captalizeString(Months[monthKey]))
            .replace('MMM', captalizeString(monthKey))
            .replace('MM', month)
            .replace('dd', day)
            .replace('HH', hours)
            .replace('mm', minutes)
            .replace('ss', seconds)
            .replace('SSSSSS', milliseconds)
            .replace('Z', timezone);
    };

    public static getDateIndependenteDoTimezone(date: Date | string) {
        const toReturn = new Date(date);
        toReturn.setMinutes(toReturn.getMinutes() + toReturn.getTimezoneOffset());
        return toReturn;
    }
}
